import {
  Dashboard as DashboardIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  keyframes,
  Paper,
  Slider,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setSuccess } from "../../Redux/AlertReducer";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../tools/api_Routes";
import ReusableSettingsCard from "./SettingCard.tsx";

// Animation keyframes
const slideIn = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Styled components
const SettingCard = styled(Paper)(({ theme }) => ({
  animation: `${slideIn} 0.3s ease-out`,
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));
const Setting = ({ data }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [settings, setSettings] = useState({});
  const [newPaymentMethod, setNewPaymentMethod] = useState("");
  useEffect(() => {
    if (data) {
      setSettings({
        notify_before_days: Number(data?.notify_before_days) || 30,
        old_domain_years: Number(data?.old_domain_years) || 5,
        payment_methods: data?.payment_methods?.split(",") || [],
      });
    }
  }, [data]);
  const handleAddPaymentMethod = () => {
    if (newPaymentMethod.trim().includes(",")) {
      alert("Commas are not allowed in payment method names");
      return;
    }
    if (
      newPaymentMethod.trim() &&
      !settings.payment_methods?.includes(newPaymentMethod.trim())
    ) {
      setSettings((prev) => ({
        ...prev,
        payment_methods: [...prev.payment_methods, newPaymentMethod.trim()],
      }));
      setNewPaymentMethod("");
    }
  };

  const handleDeletePaymentMethod = (method) => {
    setSettings((prev) => ({
      ...prev,
      payment_methods: prev.payment_methods.filter((m) => m !== method),
    }));
  };
  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const handleSave = async () => {
    setIsLoadingSubmit(true);
    const formattedSettings = {
      ...settings,
      payment_methods: settings.payment_methods?.join(",") || "",
    };
    const payload = {
      settings: Object.entries(formattedSettings).map(([key, value]) => ({
        key,
        value: typeof value === "number" ? value.toString() : value,
      })),
    };

    try {
      const { response } = await Helper.Post({
        url: api_Routes.Settings.Edit,
        data: payload,
      });

      setIsLoadingSubmit(false);
      if (response) {
        dispatch(setSuccess(true));
        navigate("/Settings");
      }
    } catch (error) {
      console.error("An error occurred during form submission:", error);
      setIsLoadingSubmit(false);
    }
  };
  const settingsConfig = [
    {
      key: "old_domain_years",
      label: "Number of years for old domains",
      value: settings.old_domain_years,
      unit: "years",
      inputProps: {
        min: 1,
        max: 100,
      },
    },
  ];
  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: "0 auto" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <SettingsIcon sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="h4" gutterBottom>
          Admin Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage application-wide settings and preferences
        </Typography>
      </Box>
      <ReusableSettingsCard
        title="General Settings"
        settings={settingsConfig}
        onChange={handleSettingChange}
        icon={<DashboardIcon />}
      />
      <SettingCard elevation={3}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <NotificationsIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Notification Settings</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Subscription Expiration Alert
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Slider
              value={settings.notify_before_days || 30}
              onChange={(e, val) =>
                handleSettingChange("notify_before_days", val)
              }
              min={1}
              max={60}
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="body1">
              {settings.notify_before_days || 30} days
            </Typography>
          </Box>
        </Box>
      </SettingCard>
      <SettingCard elevation={3}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <DashboardIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Payment Methods</Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Available Payment Methods
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="New Payment Method"
              value={newPaymentMethod}
              onChange={(e) => setNewPaymentMethod(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAddPaymentMethod()}
            />
            <Button
              variant="contained"
              onClick={handleAddPaymentMethod}
              disabled={!newPaymentMethod.trim()}
            >
              Add
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {settings.payment_methods?.map((method) => (
              <Paper
                key={method}
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Typography>{method}</Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeletePaymentMethod(method)}
                >
                  Delete
                </Button>
              </Paper>
            ))}
          </Box>
        </Box>
      </SettingCard>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          sx={{ px: 4, py: 1.5 }}
        >
          {isLoadingSubmit ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
};
export default Setting;
